import { useEffect, useState } from "react";
import { PRIVACY } from "@/constants/testIds";

const KEY = "waylio-privacy-consent";

function loadConsent() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function PrivacyBar() {
  const [consent, setConsent] = useState(null);
  const [ready, setReady] = useState(false);
  const [managing, setManaging] = useState(false);
  const gpc =
    typeof navigator !== "undefined" && navigator.globalPrivacyControl === true;
  const [analytics, setAnalytics] = useState(true);
  const [ads, setAds] = useState(!gpc);

  useEffect(() => {
    setConsent(loadConsent());
    setReady(true);
  }, []);

  const save = (c) => {
    try {
      localStorage.setItem(
        KEY,
        JSON.stringify({ ...c, ts: new Date().toISOString() })
      );
    } catch {
      // storage unavailable — still honor the choice for this session
    }
    setConsent(c);
    setManaging(false);
  };

  if (!ready || (consent && !managing)) return null;

  return (
    <div
      data-testid={PRIVACY.root}
      role="region"
      aria-label="Privacy choices"
      className="fixed bottom-0 inset-x-0 z-[70] bg-forest text-cream border-t-2 border-terracotta"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 md:py-5">
        <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
          <p className="text-xs md:text-sm leading-relaxed text-cream/85 md:flex-1">
            Waylio uses cookies to make your visit smoother and to understand
            how our site is used. We may also share details about your visit —
            including through third-party cookies — with partners for
            personalized advertising and other purposes described in our
            Privacy Policy. Depending on where you live, you may have the right
            to opt out of certain sharing for targeted advertising. Choose
            "Manage Preferences" to review or update your choices anytime, or
            use the Global Privacy Control signal to opt out automatically.
          </p>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <button
              type="button"
              data-testid={PRIVACY.accept}
              onClick={() => save({ analytics: true, advertising: true })}
              className="bg-terracotta hover:bg-terracotta-hover text-cream px-5 py-2.5 text-sm font-bold transition-colors"
            >
              Accept All
            </button>
            <button
              type="button"
              data-testid={PRIVACY.reject}
              onClick={() => save({ analytics: false, advertising: false })}
              className="border border-cream/40 text-cream px-5 py-2.5 text-sm font-bold hover:bg-cream hover:text-forest transition-colors"
            >
              Reject All
            </button>
            <button
              type="button"
              data-testid={PRIVACY.manage}
              onClick={() => setManaging((v) => !v)}
              className="text-cream/80 underline underline-offset-4 decoration-cream/40 hover:text-terracotta px-2 py-2.5 text-sm font-medium transition-colors"
            >
              Manage Preferences
            </button>
          </div>
        </div>

        {managing && (
          <div className="mt-4 pt-4 border-t border-cream/15 flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
            <label className="flex items-center gap-3 text-sm text-cream/90">
              <input
                type="checkbox"
                data-testid={PRIVACY.toggleAnalytics}
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                className="w-4 h-4 accent-terracotta"
              />
              Analytics &amp; research cookies
            </label>
            <label className="flex items-center gap-3 text-sm text-cream/90">
              <input
                type="checkbox"
                data-testid={PRIVACY.toggleAds}
                checked={ads}
                onChange={(e) => setAds(e.target.checked)}
                className="w-4 h-4 accent-terracotta"
              />
              Targeted advertising &amp; third-party sharing
            </label>
            {gpc && (
              <span className="text-xs text-cream/60">
                Global Privacy Control detected — advertising sharing is off by
                default.
              </span>
            )}
            <button
              type="button"
              data-testid={PRIVACY.save}
              onClick={() => save({ analytics, advertising: ads })}
              className="bg-terracotta hover:bg-terracotta-hover text-cream px-5 py-2.5 text-sm font-bold transition-colors md:ml-auto"
            >
              Save Preferences
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
